import { useCallback, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SectionWrapperContent from "../../../hocs/SectionWrapper/SectionWrapperContent";
import { PAGES } from "../../../../constants/pages";
import Typography from "../../../ui/typography";
import { useGetMenuCategoriesQuery } from "../../../../generated/graphql";
import Loader from "../../../ui/loader";
import arrowDown from "../../../../assets/images/arrow-down.png";
import { cn } from "../../../../utils";

function MenuBarContent() {
  const { t, i18n } = useTranslation();
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const { data, error, loading } = useGetMenuCategoriesQuery({
    fetchPolicy: "cache-first",
    variables: {
      locale: i18n.language,
      pagination: {
        limit: -1,
      },
      filters: {
        showInBurgerMenu: {
          eq: true,
        },
      },
    },
  });

  const onMenuCategoryClick = useCallback(
    (categoryId: string | undefined) => {
      if (!categoryId) return;
      if (expandedCategories.find((c) => c === categoryId))
        setExpandedCategories(
          expandedCategories.filter((c) => c !== categoryId)
        );
      else setExpandedCategories([...expandedCategories, categoryId]);
    },
    [expandedCategories]
  );

  if (loading)
    return (
      <div className="flex w-full justify-center">
        <Loader />
      </div>
    );

  if (error || !data) return null;

  const { menuCategories } = data;

  return (
    <SectionWrapperContent
      wrapperClassName="xl:w-full h-full"
      className="max-h-[90%] lg:overflow-y-auto"
    >
      {[...menuCategories]
        .filter(
          (menuCategory) =>
            menuCategory?.menu_pages && menuCategory?.menu_pages?.length > 0
        )
        .sort((a, b) => {
          return (a?.priority ?? Infinity) - (b?.priority ?? Infinity);
        })
        .map((menuCategory) => (
          <div key={menuCategory?.documentId} className="mb-4">
            <div
              onClick={() => onMenuCategoryClick(menuCategory?.documentId)}
              className="cursor-pointer flex justify-between items-center"
            >
              <Typography className="mb-1" variant="menuSubtitle">
                {menuCategory?.name}
              </Typography>
              {menuCategory?.menu_pages &&
                menuCategory.menu_pages.length > 0 && (
                  <img
                    alt="Expand arrow"
                    src={arrowDown}
                    className={cn(
                      "transition-all duration-300",
                      menuCategory?.documentId &&
                        expandedCategories.indexOf(menuCategory?.documentId) !==
                          -1
                        ? "rotate-180"
                        : ""
                    )}
                  />
                )}
            </div>
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={
                menuCategory
                  ? {
                      opacity: expandedCategories.includes(
                        menuCategory?.documentId
                      )
                        ? 1
                        : 0,
                      height: expandedCategories.includes(
                        menuCategory?.documentId
                      )
                        ? "auto"
                        : 0,
                    }
                  : {}
              }
              exit={{
                opacity: 0,
                height: 0,
              }}
              transition={{ duration: 0.3 }}
            >
              {expandedCategories.find(
                (c) => c === menuCategory?.documentId
              ) && (
                <ul className="my-4">
                  {(menuCategory?.slug === "educatie-and-inspiratie" ||
                    menuCategory?.slug === "aide-et-conseils") && (
                    <li>
                      <Link
                        className="text-black no-underline hover:underline"
                        to={PAGES.EVENTS}
                      >
                        {t("menu.events")}
                      </Link>
                    </li>
                  )}
                  {menuCategory?.menu_pages &&
                    [...menuCategory?.menu_pages]
                      .sort((a, b) => {
                        return (
                          (a?.showInBurgerMenuPriority ?? Infinity) -
                          (b?.showInBurgerMenuPriority ?? Infinity)
                        );
                      })
                      .map((page) => (
                        <li key={page?.documentId}>
                          <Link
                            className="text-black no-underline hover:underline"
                            to={
                              page?.slug
                                ? `${PAGES.MENU}/${i18n.language}/${page?.slug}`
                                : ""
                            }
                          >
                            {page?.title}
                          </Link>
                        </li>
                      ))}
                </ul>
              )}
            </motion.div>
          </div>
        ))}
    </SectionWrapperContent>
  );
}

export default MenuBarContent;
